'use client';
import React, { useEffect, useRef, useState } from 'react';

export default function CTASection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.3 }
    );
    if (ref?.current) observer?.observe(ref?.current);
    return () => observer?.disconnect();
  }, []);

  const handleScrollToContact = () => {
    const el = document.getElementById('contact');
    if (el) el?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      className="py-20 relative overflow-hidden"
      style={{ backgroundColor: '#E8F9FF', fontFamily: 'Ubuntu, sans-serif' }}
      ref={ref}
    >
      {/* Decorative elements */}
      <div className="absolute top-0 right-0 w-64 h-64 rounded-full opacity-20" style={{ background: 'radial-gradient(circle, #00AAE2, transparent)', transform: 'translate(30%, -30%)' }} />
      <div className="absolute bottom-0 left-0 w-48 h-48 rounded-full opacity-10" style={{ background: 'radial-gradient(circle, #062543, transparent)', transform: 'translate(-30%, 30%)' }} />
      <div className="max-w-[1200px] mx-auto px-4 md:px-8 relative z-10">
        <div
          className={`flex flex-col lg:flex-row items-center justify-between gap-10 transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          {/* Left */}
          <div className="max-w-[600px]">
            <p className="text-[#00AAE2] text-[15px] font-medium tracking-[2px] uppercase mb-3">Gain Success With Us</p>
            <h2
              className="text-[#062543] font-medium mb-4"
              style={{ fontSize: '42px', lineHeight: '52px', fontFamily: 'Ubuntu, sans-serif' }}
            >
              Need More Car Parking Space for Your Project?
            </h2>
            <p
              className="text-[#3A3A3A] mb-2"
              style={{ fontSize: '18px', lineHeight: '28px', fontFamily: 'Ubuntu, sans-serif' }}
            >
              Our Consultants Will Help You
            </p>
            <p
              className="text-[#3A3A3A]"
              style={{ fontSize: '16px', lineHeight: '26px', fontFamily: 'Ubuntu, sans-serif' }}
            >
              We have a dedicated design team to take care of your car parking requirements. From feasibility study to final installation, we are your complete parking partner.
            </p>
          </div>

          {/* Right: CTA */}
          <div className="flex flex-col gap-4 items-center lg:items-end">
            <div style={{ display: 'inline-block', transform: 'skew(-15deg, 0deg)' }}>
              <a
                href="#contact"
                onClick={(e) => { e?.preventDefault(); handleScrollToContact(); }}
                className="relative inline-block overflow-hidden bg-[#062543] border border-[#062543] group"
                style={{ padding: '1px', transition: '0.5s' }}
              >
                <span
                  className="relative z-10 block px-8 py-4 text-white text-[18px] font-medium"
                  style={{ transform: 'skew(15deg, 0deg)', fontFamily: 'Ubuntu, sans-serif', transition: '0.5s' }}
                >
                  Become a Client
                </span>
                <span
                  className="absolute rounded-full bg-[#00AAE2] group-hover:scale-[10] transition-transform duration-700"
                  style={{ width: '10px', height: '10px', top: '50%', left: '50%', transform: 'translate(-50%, -50%) scale(0)', transformOrigin: 'center' }}
                />
              </a>
            </div>
            <p className="text-[#3A3A3A] text-[14px]" style={{ fontFamily: 'Ubuntu, sans-serif' }}>
              Free consultation available
            </p>
          </div>
        </div>

        {/* Services list */}
        <div
          className={`mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 transition-all duration-700 delay-300 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          {[
            'Stack Parking System',
            'Puzzle Parking System',
            'Tower Parking System',
            'Horizontal Circulation',
            'Shuttle / Robotic Parking',
            'Rotary Parking System',
          ]?.map((service, i) => (
            <a
              key={i}
              href="#contact"
              onClick={(e) => { e?.preventDefault(); handleScrollToContact(); }}
              className="flex items-center gap-2 text-[#062543] hover:text-[#00AAE2] transition-colors duration-300 group"
              style={{ fontSize: '14px', fontFamily: 'Ubuntu, sans-serif' }}
            >
              <span className="w-2 h-2 rounded-full bg-[#00AAE2] flex-shrink-0 group-hover:scale-125 transition-transform" />
              {service}
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}